require("./threepl-edit-train-voucher-view.scss");

import {ChangeDetectionStrategy, Component, OnInit, inject, input, output, signal} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {BzDialogService} from "@bringoz/ui";
import {bzIconArrowLeft, bzIconHash, bzIconPrintOutline} from "@bringoz/ui/icons";
import * as _ from "lodash";
import {TrainContainerDetails} from "../../../models/train-container-details.model";
import {TrainCarDetails} from "../../../models/train-car-details.model";
import {TrainVoucher} from "../../models/train-voucher.model";
import {HazardousMaterial, TrainCar, TrainStation, TrainProviderExternalData} from "../../models/train-provider-settings.model";
import {ThreePLTrainService} from "../../services/threepl-train.service";
import {trainCarKey} from "../../models/train-car-key";
import {ContainerChange, SharedCarChange} from "../threepl-train-voucher-containers-table/threepl-train-voucher-containers-table.component";

function parseJsonValues<T>(json: string | undefined): T[] {
  return json ? _.values(JSON.parse(json)) as T[] : [];
}

@Component({
  selector: "app-threepl-edit-train-voucher-view",
  templateUrl: "./threepl-edit-train-voucher-view.html",
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ThreeplEditTrainVoucherViewComponent implements OnInit {
  readonly voucher = input.required<TrainVoucher>();

  readonly back = output<void>();
  readonly updateVoucher = output<TrainVoucher>();

  readonly icons = {back: bzIconArrowLeft, print: bzIconPrintOutline, hash: bzIconHash};

  readonly containers = signal<TrainContainerDetails[]>([]);
  readonly trainCars = signal<TrainCar[]>([]);
  readonly hazardousMaterials = signal<HazardousMaterial[]>([]);
  readonly trainStations = signal<TrainStation[]>([]);
  readonly sharedCarsMap = signal<Record<string, boolean>>({});
  readonly totalCars = signal<number | undefined>(undefined);

  private readonly trainService = inject(ThreePLTrainService);
  private readonly dialog = inject(BzDialogService);
  private readonly translate = inject(TranslateService);

  ngOnInit(): void {
    const voucher = this.voucher();
    this.totalCars.set(voucher.threePlExtraData.totalCars);
    this.containers.set(_.flatMap(voucher.threePlExtraData.carsDetails, (car: TrainCarDetails) =>
      _.map(car.trainContainerDetails, (container: TrainContainerDetails) => {
        container.carType = car.carType;
        container.carId = trainCarKey(car);
        return container;
      })));
    this.loadTrainSettings();
  }

  onAddContainer(): void {
    this.containers.update((containers) => _.concat(containers, [this.trainService.getEmptyContainer()]));
  }

  onChangeContainer({container, index}: ContainerChange): void {
    const copy = _.cloneDeep(this.containers());
    copy.splice(index, 1, container);
    this.containers.set(copy);
    this.setTotalCars();
  }

  onIsSharedCarChange({carId, isSharedCar}: SharedCarChange): void {
    const containersOnSameCar = this.containers().filter((c) => c.carId === carId);
    if (containersOnSameCar.length > 1) {
      this.dialog.confirm({
        title: `Shared ${carId}`,
        message: this.translate.instant("THREEPL_TRAIN_SHARED_CAR_INFO"),
        confirmLabel: this.translate.instant("Confirm"),
        cancelLabel: this.translate.instant("Cancel"),
      }).then((confirmed) => {
        if (confirmed) {
          this.setShared(carId, isSharedCar);
        }
      });
    } else {
      this.setShared(carId, isSharedCar);
    }
  }

  onRemoveContainer({index}: ContainerChange): void {
    const copy = _.cloneDeep(this.containers());
    copy.splice(index, 1);
    this.containers.set(copy);
    this.setTotalCars();
  }

  onPrintVoucher(): void {
    this.trainService.printVoucher([this.voucher().id]);
  }

  onUpdate(): void {
    const voucher = this.voucher();
    const containersByCar = _.groupBy(this.containers(), "carId");
    voucher.threePlExtraData.carsDetails = [];
    _.forOwn(containersByCar, (containers, carId) => {
      const carsDetailsIndex = _.findIndex(voucher.threePlExtraData.carsDetails, (car: TrainCarDetails) => car.id === carId);
      if (carsDetailsIndex > -1) {
        voucher.threePlExtraData.carsDetails[carsDetailsIndex].trainContainerDetails = containers;
      } else {
        const carSettings = _.find(this.trainCars(), (car) => car.carId === carId);
        voucher.threePlExtraData.carsDetails.push({
          id: carId,
          carType: carSettings.carType,
          allowedWeightInTon: carSettings.capacity ? _.toNumber(carSettings.capacity) : undefined,
          trainContainerDetails: containers,
          shared: this.sharedCarsMap()[carSettings.carId],
        });
      }
    });
    this.updateVoucher.emit(voucher);
  }

  private setShared(carId: string, isSharedCar: boolean): void {
    this.sharedCarsMap.update((map) => ({...map, [carId]: isSharedCar}));
  }

  private setTotalCars(): void {
    const totalCars = _.uniq(this.containers().map((c) => c.carId)).length;
    this.voucher().threePlExtraData.totalCars = totalCars;
    this.totalCars.set(totalCars);
  }

  private loadTrainSettings(): void {
    this.trainService.getTrainProviderExternalData().then((settings: TrainProviderExternalData) => {
      this.hazardousMaterials.set(parseJsonValues<HazardousMaterial>(settings.hazardousMaterialsJson));
      this.trainStations.set(parseJsonValues<TrainStation>(settings.trainStationsJson).map((s) => ({...s, stationId: s.id})));

      const trainCars = parseJsonValues<TrainCar>(settings.trainCarTypeJson)
        .map((car) => ({...car, carId: trainCarKey(car, car.capacity)}));
      this.voucher().threePlExtraData.carsDetails?.forEach((car) => {
        if (!car.shared) {
          return;
        }
        const match = trainCars.find((tc) => tc.carId === trainCarKey(car));
        if (match) {
          match.shared = true;
        }
      });
      this.trainCars.set(trainCars);
    }).catch(() => undefined);
  }
}
